import React, { useState, useEffect, useMemo } from 'react';
import { Product } from '../types';
import { getInventory, decreaseQuantity, increaseQuantity } from '../utils/storage';
import SvgIcon from '../utils/svgIcons';

interface InventoryProps {
  onEditProduct: (product: Product) => void;
}

const Inventory: React.FC<InventoryProps> = ({ onEditProduct }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<'default' | 'expiryAsc' | 'expiryDesc'>('default');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = () => {
    const inventory = getInventory();
    setProducts(inventory);
  };

  const handleIncreaseQuantity = (id: string) => {
    increaseQuantity(id);
    loadInventory();
  };

  const handleDecreaseQuantity = (id: string) => {
    decreaseQuantity(id);
    loadInventory();
  };

  const categories = useMemo(
    () => Array.from(new Set(products.map(p => p.category).filter((c): c is string => !!c))),
    [products]
  );

  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const cat = categoryFilter;
    return products.filter(p => {
      const searchMatch =
        p.name.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term);
      const categoryMatch = cat === 'all' || (p.category || '') === cat;
      return searchMatch && categoryMatch;
    });
  }, [products, searchTerm, categoryFilter]);

  const sortedProducts = useMemo(() => {
    if (sortOption === 'default') return filteredProducts;
    const arr = [...filteredProducts];
    arr.sort((a, b) => {
      const aDate = a.expiryDate ? new Date(a.expiryDate).getTime() : Number.POSITIVE_INFINITY;
      const bDate = b.expiryDate ? new Date(b.expiryDate).getTime() : Number.POSITIVE_INFINITY;
      if (sortOption === 'expiryAsc') return aDate - bDate;
      if (sortOption === 'expiryDesc') return bDate - aDate;
      return 0;
    });
    return arr;
  }, [filteredProducts, sortOption]);

  return (
    <div className="app-container inventory">
      <h2>Stock</h2>
      
      <div className="search-bar">
        <div className="flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setSortOption(prev =>
                prev === 'expiryAsc' ? 'expiryDesc' : 'expiryAsc'
              )
            }
          >
            EXP. DATE
            {sortOption === 'expiryDesc' ? (
              <SvgIcon name="arrow-down" className="nav-icon" />
            ) : (
              <SvgIcon name="arrow-up" className="nav-icon" />
            )}
          </button>
        </div>
        <div className="category-scroll">
          <button
            type="button"
            className={`category-chip ${categoryFilter === 'all' ? 'active' : ''}`}
            onClick={() => setCategoryFilter('all')}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              type="button"
              key={cat}
              className={`category-chip ${categoryFilter === cat ? 'active' : ''}`}
              onClick={() => setCategoryFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
      
      {sortedProducts.length === 0 ? (
        <div className="no-products">
          {searchTerm ? 'No products found' : 'No products in stock'}
        </div>
      ) : (
        <div className="product-list">
          {sortedProducts.map(product => (
            <div 
              key={product.id} 
              className={`product-item ${product.quantity < 5 ? 'low-stock' : ''}`}
            >
              <div className="product-info">
                <h3>
                  {product.name}
                  {product.expiryDate && (
                    <span className="product-expiry">
                      Exp: {product.expiryDate}
                    </span>
                  )}
                </h3>
                <span className={`product-quantity ${product.quantity < 5 ? 'warning' : ''}`}>
                  {product.quantity < 5 && <SvgIcon name="circle-alert" size={28} className="nav-icon" />}
                  Quantity: {product.quantity}
                </span>
              </div>
              
              <div className="product-actions">
                <button  
                  onClick={() => handleIncreaseQuantity(product.id)}
                >
                  <SvgIcon name="plus" size={28} className="nav-icon" />
                </button>
                <button 
                  onClick={() => handleDecreaseQuantity(product.id)}
                  disabled={product.quantity <= 0}
                >
                  <SvgIcon name="minus" size={28} className="nav-icon" />
          </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Inventory; 